import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { CoachingService, MyCoach, productLineLabel } from './coaching.service';
import { CoachingMessageEvent, CoachingSocketService, CoachingUnknownWhatsAppEvent } from './coaching-socket.service';

const UNREAD_POLL_MS = 60000;
const SOUND_COOLDOWN_MS = 2500;

/**
 * App-wide coaching notifications: Socket.IO primary, unread poll fallback,
 * soft chime + toast when a message arrives for a thread that isn't focused.
 */
@Injectable({ providedIn: 'root' })
export class CoachingRealtimeService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly messageSubject = new Subject<CoachingMessageEvent>();
  private readonly userUnreadSubject = new BehaviorSubject<number>(0);
  private readonly coachUnreadSubject = new BehaviorSubject<number>(0);
  private readonly drawerOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly coachesSubject = new BehaviorSubject<MyCoach[]>([]);
  private readonly openThreadRequest = new Subject<string>();
  private readonly unknownSubject = new Subject<CoachingUnknownWhatsAppEvent>();
  private readonly unknownCountSubject = new BehaviorSubject<number>(0);

  /** Thread currently open in inbox / messenger — no toast/sound for that thread. */
  private focusedThreadId: string | null = null;
  private soundMuted = false;
  private lastSoundAt = 0;
  private started = false;
  private unreadPoll?: Subscription;
  private socketSubs?: Subscription;
  private audioCtx: AudioContext | null = null;

  readonly messages$ = this.messageSubject.asObservable();
  /** Unread coach→user messages (messenger FAB). */
  readonly userUnreadTotal$ = this.userUnreadSubject.asObservable();
  /** Unread user→coach messages (coach inbox badge). */
  readonly coachUnreadTotal$ = this.coachUnreadSubject.asObservable();
  readonly drawerOpen$ = this.drawerOpenSubject.asObservable();
  readonly coaches$ = this.coachesSubject.asObservable();
  /** Messenger should open this thread id (from toast / my-coaches). */
  readonly openThreadRequest$ = this.openThreadRequest.asObservable();
  readonly unknownWhatsApp$ = this.unknownSubject.asObservable();
  readonly unknownCount$ = this.unknownCountSubject.asObservable();

  constructor(
    private auth: AuthService,
    private socket: CoachingSocketService,
    private coaching: CoachingService,
    private toast: ToastService
  ) {
    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      if (user) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get userUnreadTotal(): number {
    return this.userUnreadSubject.value;
  }

  get drawerOpen(): boolean {
    return this.drawerOpenSubject.value;
  }

  get socketConnected(): boolean {
    return this.socket.isConnected;
  }

  start(): void {
    if (this.started || !this.auth.getToken()) {
      return;
    }
    this.started = true;
    this.socket.connect();

    this.socketSubs?.unsubscribe();
    this.socketSubs = new Subscription();
    this.socketSubs.add(
      this.socket.messages$.subscribe(event => this.onMessage(event))
    );

    const staff = this.isStaffCoach();
    if (staff) {
      this.socketSubs.add(
        this.socket.newClients$.subscribe(() => {
          this.refreshCoachUnread();
          this.toast.showInfo('A new client was assigned to you', 'Coaching');
          this.playChime();
        })
      );
      this.socketSubs.add(
        this.socket.unknownWhatsApp$.subscribe(event => {
          this.unknownSubject.next(event);
          this.unknownCountSubject.next(this.unknownCountSubject.value + 1);
          this.toast.showInfo(
            `${event.display_number || event.phone_digits}: ${(event.content || '').slice(0, 60)}`,
            'Unknown WhatsApp number',
            6000
          );
          this.playChime();
        })
      );
      this.refreshCoachUnread();
      this.refreshUnknownCount();
    } else {
      // End users only: my-coaches messenger state (never hit admin/trainer unread APIs).
      this.refreshCoaches();
      this.refreshUserUnread();
    }

    this.unreadPoll?.unsubscribe();
    this.unreadPoll = interval(UNREAD_POLL_MS).subscribe(() => {
      if (document.hidden) {
        return;
      }
      if (this.isStaffCoach()) {
        this.refreshCoachUnread();
      } else {
        this.refreshUserUnread();
      }
    });
  }

  stop(): void {
    this.started = false;
    this.unreadPoll?.unsubscribe();
    this.socketSubs?.unsubscribe();
    this.socketSubs = undefined;
    this.socket.disconnect();
    this.userUnreadSubject.next(0);
    this.coachUnreadSubject.next(0);
    this.unknownCountSubject.next(0);
    this.coachesSubject.next([]);
    this.focusedThreadId = null;
    this.drawerOpenSubject.next(false);
  }

  refreshUnknownCount(): void {
    if (!this.isStaffCoach()) {
      return;
    }
    const mode = this.coachApiMode();
    this.coaching.getUnknownWhatsAppCount(mode).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ count }) => this.unknownCountSubject.next(count),
      error: () => { /* staff without access — ignore */ }
    });
  }

  /** Admin or trainer — eligible for coach unread dock / staff APIs. */
  isStaffCoach(): boolean {
    const roles = this.auth.getRoles().map(r => String(r).toUpperCase());
    return roles.includes('ADMIN') || roles.includes('TRAINER');
  }

  bumpUnknownResolved(delta = -1): void {
    this.unknownCountSubject.next(Math.max(0, this.unknownCountSubject.value + delta));
  }

  setFocusedThread(threadId: string | null): void {
    this.focusedThreadId = threadId;
  }

  openDrawer(threadId?: string | null): void {
    this.drawerOpenSubject.next(true);
    if (threadId) {
      this.setFocusedThread(threadId);
      this.openThreadRequest.next(threadId);
    }
  }

  closeDrawer(): void {
    this.drawerOpenSubject.next(false);
    this.setFocusedThread(null);
  }

  toggleDrawer(): void {
    if (this.drawerOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  setMuted(muted: boolean): void {
    this.soundMuted = muted;
  }

  /** Call after the user opens a thread so the FAB badge drops. */
  clearThreadUnread(threadId: string): void {
    const coaches = this.coachesSubject.value.map(c =>
      c.thread_id === threadId ? { ...c, unread_count: 0 } : c
    );
    this.coachesSubject.next(coaches);
    this.userUnreadSubject.next(coaches.reduce((sum, c) => sum + (c.unread_count || 0), 0));
  }

  refreshCoaches(): void {
    this.coaching.getMyCoaches().pipe(takeUntil(this.destroy$)).subscribe({
      next: coaches => {
        this.coachesSubject.next(coaches);
        this.userUnreadSubject.next(coaches.reduce((sum, c) => sum + (c.unread_count || 0), 0));
      },
      error: () => { /* non-fatal */ }
    });
  }

  private onMessage(event: CoachingMessageEvent): void {
    this.messageSubject.next(event);

    const myId = this.auth.currentUserId;
    const fromMe = !!myId && event.message.sender_id === myId;
    const focused = this.focusedThreadId === event.thread_id;

    if (fromMe) {
      return;
    }

    if (!focused) {
      this.bumpUnread(event);
      this.notify(event);
    }
  }

  private bumpUnread(event: CoachingMessageEvent): void {
    if (event.message.from_user) {
      this.coachUnreadSubject.next(this.coachUnreadSubject.value + 1);
      return;
    }

    const coaches = this.coachesSubject.value.map(c => {
      if (c.thread_id !== event.thread_id) {
        return c;
      }
      return { ...c, unread_count: (c.unread_count || 0) + 1 };
    });
    this.coachesSubject.next(coaches);
    this.userUnreadSubject.next(coaches.reduce((sum, c) => sum + (c.unread_count || 0), 0));
  }

  private notify(event: CoachingMessageEvent): void {
    const preview = (event.message.content || '').trim().slice(0, 80);
    const line = event.product_line ? productLineLabel(event.product_line) : 'Coaching';
    const title = event.message.from_user ? 'New client message' : `${line} coach`;
    this.toast.showInfo(preview || 'New message', title, 4000);
    this.playChime();
  }

  private refreshUserUnread(): void {
    if (this.isStaffCoach()) {
      return;
    }
    this.coaching.getMyUnreadTotal().pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ unread }) => this.userUnreadSubject.next(unread),
      error: () => { /* user may not have coaching */ }
    });
  }

  private refreshCoachUnread(): void {
    if (!this.isStaffCoach()) {
      return;
    }
    // Admins use /admin/coaching; TRAINER role uses /manage-trainer/coaching.
    const mode = this.coachApiMode();
    this.coaching.getUnreadTotal(mode).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ unread }) => this.coachUnreadSubject.next(unread),
      error: () => { /* not authorized for this mode — ignore */ }
    });
  }

  private coachApiMode(): 'admin' | 'coach' {
    const roles = this.auth.getRoles().map(r => String(r).toUpperCase());
    if (roles.includes('ADMIN')) {
      return 'admin';
    }
    return 'coach';
  }

  private playChime(): void {
    if (this.soundMuted || document.hidden) {
      return;
    }
    const now = Date.now();
    if (now - this.lastSoundAt < SOUND_COOLDOWN_MS) {
      return;
    }
    this.lastSoundAt = now;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) {
        return;
      }
      if (!this.audioCtx) {
        this.audioCtx = new Ctx();
      }
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch {
      /* autoplay / audio blocked — ignore */
    }
  }
}
